/**
 * Image Presets Dialog Component
 *
 * Full-page dialog for viewing, searching, filtering, and managing image presets.
 * Replaces the narrow ImagePresetsSheet with a responsive grid layout,
 * search, tag filtering, and inline editing.
 */

import { useState, useMemo } from 'react';
import { BookMarked, Search, Settings2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { PresetCard } from './PresetCard';
import { ManageTagsCategoriesDialog } from './ManageTagsCategoriesDialog';
import type { ImagePreset, TagDefinition } from '../types/image-generator.types';

export interface ImagePresetsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  presets: ImagePreset[];
  tagDefinitions: TagDefinition[];
  sizeOptions: { value: string; label: string }[];
  qualityOptions: { value: string; label: string }[];
  styleOptions: { value: string; label: string }[];
  onLoadPreset: (preset: ImagePreset) => void;
  onDeletePreset: (presetId: string) => void;
  onUpdatePreset: (
    presetId: string,
    updates: Partial<Pick<ImagePreset, 'tags' | 'categoryId'>>,
  ) => void;
  onAddTag: (name: string) => string;
  onDeleteTag: (tagId: string) => void;
  onRenameTag: (tagId: string, name: string) => void;
}

export function ImagePresetsDialog({
  open,
  onOpenChange,
  presets,
  tagDefinitions,
  sizeOptions,
  qualityOptions,
  styleOptions,
  onLoadPreset,
  onDeletePreset,
  onUpdatePreset,
  onAddTag,
  onDeleteTag,
  onRenameTag,
}: ImagePresetsDialogProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTagFilters, setSelectedTagFilters] = useState<string[]>([]);
  const [isManageOpen, setIsManageOpen] = useState(false);

  const toggleTagFilter = (tagId: string) => {
    setSelectedTagFilters((prev) =>
      prev.includes(tagId) ? prev.filter((id) => id !== tagId) : [...prev, tagId],
    );
  };

  const filteredPresets = useMemo(() => {
    let result = presets;

    // Text search
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter((preset) => {
        if (preset.name.toLowerCase().includes(query)) return true;
        // Check tag names
        const presetTagNames = tagDefinitions
          .filter((t) => preset.tags.includes(t.id))
          .map((t) => t.name.toLowerCase());
        if (presetTagNames.some((name) => name.includes(query))) return true;
        return false;
      });
    }

    // Tag filter (AND logic)
    if (selectedTagFilters.length > 0) {
      result = result.filter((p) => selectedTagFilters.every((tagId) => p.tags.includes(tagId)));
    }

    return result;
  }, [presets, searchQuery, selectedTagFilters, tagDefinitions]);

  const handleCreateTag = (name: string): string => {
    return onAddTag(name);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-6xl w-[95vw] h-[90vh] flex flex-col p-0">
          <DialogHeader className="flex-shrink-0 px-6 pt-6 pb-4 border-b border-border">
            <div className="flex items-center justify-between">
              <DialogTitle className="text-lg">Saved Presets</DialogTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsManageOpen(true)}
                className="gap-1.5 mr-8"
              >
                <Settings2 className="h-4 w-4" />
                Manage Tags
              </Button>
            </div>

            {/* Search */}
            <div className="relative mt-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search presets by name or tag..."
                className="pl-9"
              />
            </div>

            {/* Tag filter */}
            {tagDefinitions.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {tagDefinitions.map((tag) => (
                  <Badge
                    key={tag.id}
                    variant={selectedTagFilters.includes(tag.id) ? 'default' : 'outline'}
                    className="cursor-pointer text-xs"
                    onClick={() => toggleTagFilter(tag.id)}
                  >
                    {tag.name}
                  </Badge>
                ))}
              </div>
            )}
          </DialogHeader>

          {/* Body: scrollable grid */}
          <div className="flex-1 overflow-y-auto px-6 py-4">
            {presets.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center py-12">
                <BookMarked className="h-16 w-16 text-muted-foreground/30 mb-4" />
                <p className="text-muted-foreground">No presets saved yet</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Save your current settings as a preset to reuse them later.
                </p>
              </div>
            ) : filteredPresets.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center py-12">
                <Search className="h-16 w-16 text-muted-foreground/30 mb-4" />
                <p className="text-muted-foreground">No presets match your filters</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Try adjusting your search or filter criteria.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {filteredPresets.map((preset) => (
                  <PresetCard
                    key={preset.id}
                    preset={preset}
                    tagDefinitions={tagDefinitions}
                    sizeOptions={sizeOptions}
                    qualityOptions={qualityOptions}
                    styleOptions={styleOptions}
                    onLoadPreset={onLoadPreset}
                    onDeletePreset={onDeletePreset}
                    onUpdatePreset={onUpdatePreset}
                    onCreateTag={handleCreateTag}
                  />
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Nested manage dialog */}
      <ManageTagsCategoriesDialog
        open={isManageOpen}
        onOpenChange={setIsManageOpen}
        tagDefinitions={tagDefinitions}
        onAddTag={onAddTag}
        onDeleteTag={onDeleteTag}
        onRenameTag={onRenameTag}
      />
    </>
  );
}
